import { apiClient } from '../../../../services/apiClient'
import i18n from 'i18next'
import {
  BuildingPermit,
  BuildingPermitSearchRequest,
  BuildingPermitSearchResponse,
  BuildingPermitVectorTileParams,
  BuildingPermitBulkRequest,
  BuildingPermitBulkResponse,
} from './buildingPermits'

const BUILDING_PERMITS_API_BASE = '/building-permits'

/**
 * Building Permits Service
 *
 * Provides access to building permit data through various endpoints including:
 * - Individual permit fetching
 * - Bulk permit fetching via /building-permits/bulk
 * - Search and filtering
 * - Vector tile data
 *
 * @example
 * // Fetch multiple permits by IDs
 * const permits = await buildingPermitsService.getBulk([1, 2, 3])
 *
 * // Fetch with specific fields only
 * const permits = await buildingPermitsService.getBulk([1, 2, 3], ['id', 'address', 'type_name'])
 *
 * // Using the enhanced bulk interface
 * const response = await buildingPermitsService.getBulkWithResponse({
 *   ids: [1, 2, 3],
 *   fields: ['id', 'address', 'owner', 'work_description']
 * })
 */
export const buildingPermitsService = {
  async search(
    params: BuildingPermitSearchRequest
  ): Promise<BuildingPermitSearchResponse> {
    const response = await apiClient.post(
      `${BUILDING_PERMITS_API_BASE}/search`,
      params
    )
    return response.data as BuildingPermitSearchResponse
  },

  async getById(id: number): Promise<BuildingPermit | null> {
    try {
      const response = await apiClient.get(`${BUILDING_PERMITS_API_BASE}/${id}`)
      return response.data as BuildingPermit
    } catch (error) {
      console.error('Error fetching building permit by ID:', error)
      return null
    }
  },

  /**
   * Get multiple building permits by IDs (bulk endpoint)
   */
  async getBulk(ids: number[], fields?: string[]): Promise<BuildingPermit[]> {
    if (!ids || ids.length === 0) {
      return []
    }

    // Limit to 100 IDs per request as per API constraints
    const limitedIds = ids.slice(0, 100)

    const params = new URLSearchParams()
    params.append('ids', limitedIds.join(','))

    if (fields && fields.length > 0) {
      params.append('fields', fields.join(','))
    }

    const url = `${BUILDING_PERMITS_API_BASE}/bulk?${params.toString()}`
    const response = await apiClient.get(url)
    return response.data
  },

  /**
   * Get multiple building permits using the bulk request interface
   */
  async getBulkWithResponse(
    request: BuildingPermitBulkRequest
  ): Promise<BuildingPermitBulkResponse> {
    if (!request.ids || request.ids.length === 0) {
      return { data: [], total: 0 }
    }

    // Limit to 100 IDs per request as per API constraints
    const limitedIds = request.ids.slice(0, 100)

    const params = new URLSearchParams()
    params.append('ids', limitedIds.join(','))

    if (request.fields && request.fields.length > 0) {
      params.append('fields', request.fields.join(','))
    }

    const url = `${BUILDING_PERMITS_API_BASE}/bulk?${params.toString()}`
    const response = await apiClient.get(url)

    // Assuming the API returns an array directly, but we wrap it in our response format
    const data = response.data as BuildingPermit[]
    return {
      data,
      total: data.length,
    }
  },

  async getVectorTile(params: BuildingPermitVectorTileParams): Promise<Blob> {
    const { field, zoom, x, y } = params
    const response = await apiClient.get(
      `${BUILDING_PERMITS_API_BASE}/vector-tile/${field}/${zoom}/${x}/${y}`
    )
    return response.data as Blob
  },

  async searchByText(query: string): Promise<BuildingPermitSearchResponse> {
    const response = await apiClient.post(
      `${BUILDING_PERMITS_API_BASE}/search/text`,
      { query, language: i18n.language }
    )
    return response.data as BuildingPermitSearchResponse
  },

  async searchByVoice(audioBlob: Blob): Promise<BuildingPermitSearchResponse> {
    const formData = new FormData()
    formData.append('audio', audioBlob)
    formData.append('language', i18n.language)

    const response = await apiClient.post(
      `${BUILDING_PERMITS_API_BASE}/search/voice`,
      formData
    )
    return response.data as BuildingPermitSearchResponse
  },
}
